import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, UserButton } from '@clerk/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Shell, type PageId, Button } from './components';
import { ThemeToggle } from './components/ThemeToggle';
import { AdminApiClient, defaultApiBase, isDashboardApiBase } from './api';
import { AppProvider } from './app-context';

const clerkJwtTemplate = import.meta.env.VITE_CLERK_JWT_TEMPLATE as string | undefined;

const PATH_TO_PAGE: Record<string, PageId> = {
  '/': 'overview',
  '/dashboard': 'overview',
  '/providers': 'providers',
  '/models': 'models',
  '/keys': 'keys',
  '/playground': 'playground',
  '/routing': 'routing',
  '/logs': 'logs',
  '/settings': 'settings',
};

function pageIdFromPath(path: string): PageId {
  const normalized = path === '/' ? path : path.replace(/\/$/, '');
  return PATH_TO_PAGE[normalized] || 'overview';
}

function readStoredValue(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoredValue(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore storage failures (private mode/quota). React state still updates.
  }
}

function usePersistentState(key: string, initial: string) {
  const [value, setValueState] = useState(() => readStoredValue(key) ?? initial);
  const setValue = (next: string) => {
    writeStoredValue(key, next);
    setValueState(next);
  };
  return [value, setValue] as const;
}

function useApiBaseState() {
  const fallback = defaultApiBase();
  const [apiBase, setApiBaseState] = useState(() => {
    const stored = readStoredValue('apx.apiBase');
    return stored && !isDashboardApiBase(stored) ? stored : fallback;
  });

  useEffect(() => {
    const stored = readStoredValue('apx.apiBase');
    if (!stored || isDashboardApiBase(stored)) {
      writeStoredValue('apx.apiBase', fallback);
    }
  }, [fallback]);

  const setApiBase = (next: string) => {
    writeStoredValue('apx.apiBase', next);
    setApiBaseState(next);
  };

  return [apiBase, setApiBase] as const;
}

/* ------------------------------------------------------------------ */
/*  Dashboard layout — receives token getter, NEVER calls useAuth()     */
/* ------------------------------------------------------------------ */
interface DashboardProps {
  /** Clerk token provider (stable ref) or undefined for admin-token mode */
  getToken?: () => Promise<string | null>;
  clerkSignedIn?: boolean;
}

function Dashboard({ getToken, clerkSignedIn = false }: DashboardProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPage = pageIdFromPath(location.pathname);

  const [apiBase, setApiBase] = useApiBaseState();
  const [adminToken, setAdminToken] = usePersistentState('apx.adminToken', '');

  /* Stable API client — only rebuilds when apiBase or adminToken changes.
     getToken is accessed through a ref so Clerk auth churn does NOT
     recreate the client and does NOT trigger re-fetch storms.          */
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const tokenProvider = useCallback(async (): Promise<string | null> => {
    if (getTokenRef.current) {
      return await getTokenRef.current();
    }
    return adminToken || null;
  }, [adminToken]);

  const api = useMemo(
    () => new AdminApiClient(apiBase, tokenProvider),
    [apiBase, tokenProvider],
  );

  const clerkEnabled = !!getToken;

  const appState = useMemo(
    () => ({
      api,
      apiBase,
      setApiBase,
      adminToken,
      setAdminToken,
      clerkEnabled,
    }),
    [api, apiBase, setApiBase, adminToken, setAdminToken, clerkEnabled],
  );

  const right = (
    <div className="header-actions">
      <ThemeToggle />
      {clerkEnabled && clerkSignedIn && <UserButton />}
      <Button variant="ghost" onClick={() => navigate('/settings')}>
        Settings
      </Button>
    </div>
  );

  return (
    <AppProvider value={appState}>
      <Shell page={currentPage} right={right}>
        <Outlet />
      </Shell>
    </AppProvider>
  );
}

/* ------------------------------------------------------------------ */
/*  Clerk session bridge — ONLY rendered when Clerk is enabled.         */
/*  Calls useAuth() and all hooks unconditionally (Rules of Hooks safe). */
/*  ProtectedRoute decides which child routes require sign-in.          */
/* ------------------------------------------------------------------ */
function ClerkSessionBridge() {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;

  const getToken = useCallback(async (): Promise<string | null> => {
    const a = authRef.current;
    if (!a.isSignedIn) return null;
    if (clerkJwtTemplate) {
      return (await a.getToken({ template: clerkJwtTemplate })) ?? (await a.getToken());
    }
    return await a.getToken();
  }, []);

  if (!auth.isLoaded) {
    return (
      <div className="login-page">
        <div className="login-card">
          <p>Loading Clerk session…</p>
        </div>
      </div>
    );
  }

  return <Dashboard getToken={getToken} clerkSignedIn={auth.isSignedIn} />;
}

/* ------------------------------------------------------------------ */
/*  Root App — decides Clerk vs admin-token mode at mount.              */
/* ------------------------------------------------------------------ */
export default function App() {
  const clerkEnabled = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  if (!clerkEnabled) {
    return <Dashboard />;
  }
  return <ClerkSessionBridge />;
}
